import { applyRefractionActions } from '@ca/common'
import { ItemValuesParams } from '@order/utils/summary/getOrderPriceSummary'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import useSession, { useCustomer, useStore } from 'hooks/useSession'
import { useCallback, useMemo } from 'react'
import { getFinalOrderSummary } from '../utils/summary/getFinalOrderSummary'
import useDeal from './useDeal'
import { useOrder, useOrderState } from './useOrderState'
import { getOrderValues, getValuesDefaults } from './values/OrderValues'
import { usePrescriptionById } from './usePrescription'
import { OrderState, resolvePd, SavedOrder } from './state/orderState'
import { OrderValues } from '@ca/common/types'

export const useSavedOrderSummary = (
  order: OrderState,
  dealIds: SavedOrder['deals']
) => {
  const base = useDeal(dealIds.base?.dealId)
  const extra = useDeal(dealIds.extra?.dealId)
  const store = useStore()

  const config = useRemoteConfig()
  const deals = useMemo(
    () => ({
      base,
      extra,
    }),
    [base, extra]
  )
  const refraction = usePrescriptionById(order.prescriptionId)?.data
  const customer = useCustomer()
  const getItemValues = useCallback(
    ({ frame, bundleType, state }: ItemValuesParams) => {
      const { pd } = order
      const deal = bundleType ? deals[bundleType] : deals.base
      const values = getOrderValues({
        order,
        frame,
        fullState: state || [],
        defaults: getValuesDefaults({
          dealCode: deal?.code,
          dealType: deal?.type,
          frames: deal?.frames,
          customer,
          store,
        }),
        bundle: bundleType,
      })
      return {
        values,
        refraction: applyRefractionActions(
          refraction,
          state || [],
          resolvePd(pd)
        ),
      } satisfies OrderValues
    },
    [order, refraction, customer, deals, store]
  )

  const steps = useOrderState((state) => state.steps)

  const summary = useMemo(() => {
    if (!config) {
      return null
    }
    return getFinalOrderSummary({
      config,
      order,
      deals,
      packageConfigs: order.packageCfg,
      getItemValues,
      steps,
    })
  }, [config, order, deals, getItemValues, steps])

  return summary
}

export const useOrderSummary = () => {
  const order = useOrder()
  const deals = useSession((s) => s.deals)
  return useSavedOrderSummary(order, {
    base: deals.upgrade ?? deals.base,
    extra: deals.extraUpgrade ?? deals.extra,
  })
}
